import React, { useState } from 'react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/PaymentRecorder.css';

function PaymentRecorder({ loan, onSuccess }) {
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();

    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (amount > loan.remainingBalance) {
      setError('Payment amount cannot exceed remaining balance');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const payment = {
        amount: amount,
        date: paymentDate,
        notes: notes,
        recordedAt: new Date().toISOString()
      };

      const newBalance = loan.remainingBalance - amount;

      await updateDoc(doc(db, 'loans', loan.id), {
        payments: arrayUnion(payment),
        remainingBalance: newBalance,
        status: newBalance === 0 ? 'paid_off' : loan.status,
        lastPaymentDate: paymentDate
      });

      setPaymentAmount('');
      setNotes('');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError('Failed to record payment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="payment-recorder">
      <h3>Record Payment</h3>

      <div className="loan-info-summary">
        <div className="info-item">
          <span className="label">Loanee:</span>
          <span className="value">{loan.loaneeName}</span>
        </div>
        <div className="info-item">
          <span className="label">Original Amount:</span>
          <span className="value">${loan.amount.toFixed(2)}</span>
        </div>
        <div className="info-item">
          <span className="label">Remaining Balance:</span>
          <span className="value highlight">${loan.remainingBalance.toFixed(2)}</span>
        </div>
        <div className="info-item">
          <span className="label">Monthly Payment:</span>
          <span className="value">${loan.monthlyPayment.toFixed(2)}</span>
        </div>
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="payment-form">
        <div className="input-group">
          <label htmlFor="payment-amount">Payment Amount ($)</label>
          <input
            id="payment-amount"
            type="number"
            step="0.01"
            min="0"
            max={loan.remainingBalance}
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            className="input-field"
            required
          />
          <small className="helper-text">
            Maximum: ${loan.remainingBalance.toFixed(2)}
          </small>
        </div>

        <div className="input-group">
          <label htmlFor="payment-date">Payment Date</label>
          <input
            id="payment-date"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="input-field"
            required
          />
        </div>

        <div className="input-group">
          <label htmlFor="payment-notes">Notes (Optional)</label>
          <textarea
            id="payment-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="textarea-field"
            rows="3"
            placeholder="Payment method, reference number, etc."
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? 'Recording...' : 'Record Payment'}
        </button>
      </form>

      {loan.payments && loan.payments.length > 0 && (
        <div className="payment-history">
          <h4>Payment History</h4>
          <div className="payment-list">
            {loan.payments.map((payment, index) => (
              <div key={index} className="payment-item">
                <div className="payment-header">
                  <span className="payment-amount">${payment.amount.toFixed(2)}</span>
                  <span className="payment-date">{new Date(payment.date).toLocaleDateString()}</span>
                </div>
                {payment.notes && (
                  <div className="payment-notes">{payment.notes}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentRecorder;
